import type {
  ActivityCockpitSnapshot,
  CockpitCostTotal,
  CockpitPhaseTurnCount,
  CockpitSkillTurnCount,
  CockpitUsageInputBreakdown,
  CockpitUsageLegacyTotals,
} from '@internal/spassu-code-contract';
import type {
  CockpitMetricBreakdownRow,
  CockpitMetricDetailsView,
  CockpitMetricLegacyNoticeView,
  CockpitMetricParcelView,
  CockpitTurnCountRow,
} from './cockpitViewModel';
import { formatCanonicalCost, formatCanonicalInteger } from './formatters';
import { humanizeCode } from './semanticLabels';

/** A parcel a source never reported stays `null`; it is rendered as "não
 * informada", never as a confirmed zero (BR-02, BR-03). */
const PARCEL_NOT_REPORTED_LABEL = 'não informada';

/**
 * Additive fields (`inputBreakdown`/`byModel`/`byPhase`/`bySkill`/`legacy` on
 * `usage`, `turnsByPhase`/`turnsBySkill` on `time`), omitted by snapshots
 * created before 2026-09-10 — `undefined` reads exactly as "no details to
 * expand", never a crash or a false zero.
 *
 * `key` on every breakdown/turn row is synthetic (index-based) rather than
 * the raw `phase` value: the control plane view must never expose the
 * internal phase vocabulary verbatim (ADR-005, UT-051), and `label` already
 * carries the humanized/relabelled text a UI renders.
 */
export function usageMetricDetails(
  id: string,
  usage: ActivityCockpitSnapshot['sections']['usage']['value'],
  time: ActivityCockpitSnapshot['sections']['time']['value'],
): CockpitMetricDetailsView | null {
  const breakdowns = usageBreakdownRows(id, usage);
  const turns = usageTurnCounts(time);
  const details = { ...breakdowns, ...turns };
  return isDetailsEmpty(details) ? null : details;
}

function usageBreakdownRows(
  id: string,
  usage: ActivityCockpitSnapshot['sections']['usage']['value'],
): Pick<
  CockpitMetricDetailsView,
  'parcels' | 'byModel' | 'byPhase' | 'bySkill' | 'legacy'
> {
  const byModel = breakdownRows(usage?.byModel, row => ({
    key: `${row.model}:${row.querySource}`,
    label: `${row.model} · ${row.querySource}`,
    usages: row.usages,
    inputTokens: row.inputTokens,
    outputTokens: row.outputTokens,
    costs: row.costs,
  }));
  const byPhase = breakdownRows(usage?.byPhase, (row, index) => ({
    key: `phase-${index}`,
    label: humanizeCode(row.phase),
    usages: row.usages,
    inputTokens: row.inputTokens,
    outputTokens: row.outputTokens,
    costs: row.costs,
  }));
  const bySkill = breakdownRows(usage?.bySkill, (row, index) => ({
    key: `skill-${index}`,
    label: row.skillKey ?? 'Sem skill',
    usages: row.usages,
    inputTokens: row.inputTokens,
    outputTokens: row.outputTokens,
    costs: row.costs,
  }));
  const legacy = usageLegacyNotice(usage?.legacy);
  const parcels =
    id === 'input-tokens' && usage ? parcelViews(usage.inputBreakdown) : null;
  return { parcels, byModel, byPhase, bySkill, legacy };
}

function usageTurnCounts(
  time: ActivityCockpitSnapshot['sections']['time']['value'],
): Pick<CockpitMetricDetailsView, 'turnsByPhase' | 'turnsBySkill'> {
  return {
    turnsByPhase: turnPhaseRows(time?.turnsByPhase),
    turnsBySkill: turnSkillRows(time?.turnsBySkill),
  };
}

function isDetailsEmpty(details: CockpitMetricDetailsView): boolean {
  return (
    details.parcels === null &&
    details.byModel.length === 0 &&
    details.byPhase.length === 0 &&
    details.bySkill.length === 0 &&
    details.legacy === null &&
    details.turnsByPhase.length === 0 &&
    details.turnsBySkill.length === 0
  );
}

/** Per-phase turn counts (US-012, AC-1): user/agent counts plus first/last
 * instant — never the turn's text (AC-2). `key` is index-based, never the
 * raw `phase` code (ADR-005). */
function turnPhaseRows(
  rows: CockpitPhaseTurnCount[] | undefined,
): CockpitTurnCountRow[] {
  return (rows ?? []).map((row, index) => ({
    key: `turn-phase-${index}`,
    label: humanizeCode(row.phase),
    user: row.user,
    agent: row.agent,
    firstAt: row.firstAt,
    lastAt: row.lastAt,
  }));
}

/** Per-skill turn counts; a usage/turn with no attributed skill still gets a
 * row labelled "Sem skill" (US-007), never dropped silently. */
function turnSkillRows(
  rows: CockpitSkillTurnCount[] | undefined,
): CockpitTurnCountRow[] {
  return (rows ?? []).map((row, index) => ({
    key: `turn-skill-${index}`,
    label: row.skillKey ?? 'Sem skill',
    user: row.user,
    agent: row.agent,
    firstAt: row.firstAt,
    lastAt: row.lastAt,
  }));
}

function parcelViews(
  breakdown: CockpitUsageInputBreakdown | undefined,
): CockpitMetricParcelView[] | null {
  if (!breakdown) return null;
  return [
    { label: 'Não cacheada', ...parcelValue(breakdown.uncached.value) },
    { label: 'Leitura de cache', ...parcelValue(breakdown.cacheRead.value) },
    {
      label: 'Escrita de cache',
      ...parcelValue(breakdown.cacheCreation.value),
    },
  ];
}

function parcelValue(value: number | null): {
  value: number | null;
  displayValue: string;
} {
  return {
    value,
    displayValue:
      value === null
        ? PARCEL_NOT_REPORTED_LABEL
        : formatCanonicalInteger(value),
  };
}

function breakdownRows<
  T extends {
    usages: number;
    inputTokens: number;
    outputTokens: number;
    costs: CockpitCostTotal[];
  },
>(
  items: Array<T> | undefined,
  present: (
    item: T,
    index: number,
  ) => Omit<CockpitMetricBreakdownRow, 'costsLabel'> & {
    costs: CockpitCostTotal[];
  },
): CockpitMetricBreakdownRow[] {
  return (items ?? []).map((item, index) => {
    const { costs, ...row } = present(item, index);
    return {
      ...row,
      costsLabel:
        costs.length > 0
          ? costs
              .map(cost => formatCanonicalCost(cost.amount, cost.currency))
              .join(' · ')
          : null,
    };
  });
}

function usageLegacyNotice(
  legacy: CockpitUsageLegacyTotals | null | undefined,
): CockpitMetricLegacyNoticeView | null {
  if (!legacy) return null;
  return {
    usages: legacy.usages,
    inputTokens: legacy.inputTokens,
    outputTokens: legacy.outputTokens,
  };
}
